import { copyNow } from '../i18n/vtoCopy';
import type { LipDetection } from '../tracking/faceLandmarker';

export interface FaceRenderTuning {
  skinStrength: number;
  cheekOpacityScale: number;
  cheekRadiusScale: number;
  cheekDrapeScale: number;
  eyeOpacityScale: number;
  eyeShapeTightness: number;
  eyeFeatherScale: number;
  eyeSparkleScale: number;
  lipOpacityScale: number;
  lipInnerHoleScale: number;
  lipFinishScale: number;
}

export type FaceQualityScenario = 'clean' | 'medium' | 'borderline';

export interface FaceQualityReport {
  blocker: string | null;
  blockerCode: 'pose' | 'mouth' | 'distance' | null;
  scenario: FaceQualityScenario;
  warnings: string[];
  tuning: FaceRenderTuning;
}

interface Point {
  x: number;
  y: number;
}

const DEFAULT_TUNING: FaceRenderTuning = {
  skinStrength: 0.4,
  cheekOpacityScale: 1,
  cheekRadiusScale: 1,
  cheekDrapeScale: 1,
  eyeOpacityScale: 1,
  eyeShapeTightness: 0,
  eyeFeatherScale: 1,
  eyeSparkleScale: 1,
  lipOpacityScale: 1,
  lipInnerHoleScale: 1,
  lipFinishScale: 1
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function pointPx(detection: LipDetection, index: number): Point | null {
  const p = detection.landmarks[index];
  if (!p) return null;
  return {
    x: p.x * detection.imageWidth,
    y: p.y * detection.imageHeight
  };
}

function distance(a: Point | null, b: Point | null): number | null {
  if (!a || !b) return null;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function landmarkBounds(detection: LipDetection): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = detection.imageWidth;
  let minY = detection.imageHeight;
  let maxX = 0;
  let maxY = 0;

  detection.landmarks.forEach((p) => {
    const x = p.x * detection.imageWidth;
    const y = p.y * detection.imageHeight;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  });

  return { minX, minY, maxX, maxY };
}

function readFaceCrop(
  source: ImageBitmap | HTMLCanvasElement | HTMLImageElement,
  detection: LipDetection,
  targetSize = 64
): ImageData | null {
  const { minX, minY, maxX, maxY } = landmarkBounds(detection);
  const width = Math.max(1, Math.round(maxX - minX));
  const height = Math.max(1, Math.round(maxY - minY));
  if (width < 20 || height < 20) return null;

  const canvas = document.createElement('canvas');
  canvas.width = targetSize;
  canvas.height = targetSize;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.drawImage(
    source,
    clamp(Math.floor(minX), 0, detection.imageWidth - 1),
    clamp(Math.floor(minY), 0, detection.imageHeight - 1),
    Math.min(width, detection.imageWidth),
    Math.min(height, detection.imageHeight),
    0,
    0,
    targetSize,
    targetSize
  );

  return ctx.getImageData(0, 0, targetSize, targetSize);
}

function sampleLighting(
  source: ImageBitmap | HTMLCanvasElement | HTMLImageElement,
  detection: LipDetection
): { mean: number; splitDelta: number; variance: number } | null {
  const img = readFaceCrop(source, detection);
  if (!img) return null;
  const targetSize = img.width;
  const { data } = img;
  let total = 0;
  let left = 0;
  let right = 0;
  let count = 0;
  let leftCount = 0;
  let rightCount = 0;
  const luminances: number[] = [];

  for (let y = 0; y < targetSize; y++) {
    for (let x = 0; x < targetSize; x++) {
      const i = (y * targetSize + x) * 4;
      const alpha = data[i + 3] ?? 0;
      if (alpha < 8) continue;
      const r = data[i] ?? 0;
      const g = data[i + 1] ?? 0;
      const b = data[i + 2] ?? 0;
      const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      luminances.push(lum);
      total += lum;
      count++;
      if (x < targetSize / 2) {
        left += lum;
        leftCount++;
      } else {
        right += lum;
        rightCount++;
      }
    }
  }

  if (count === 0 || leftCount === 0 || rightCount === 0) return null;
  const mean = total / count;
  let variance = 0;
  for (const lum of luminances) {
    const d = lum - mean;
    variance += d * d;
  }
  variance /= luminances.length;

  return {
    mean,
    splitDelta: Math.abs(left / leftCount - right / rightCount),
    variance
  };
}

// Simple sharpness proxy on the face crop: mean absolute Laplacian response.
// Values are intentionally used only for soft decisions (preset + warning),
// never as a hard blocker.
function sampleBlurScore(
  source: ImageBitmap | HTMLCanvasElement | HTMLImageElement,
  detection: LipDetection
): number | null {
  const img = readFaceCrop(source, detection);
  if (!img) return null;
  const size = img.width;
  const gray = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const r = img.data[i] ?? 0;
      const g = img.data[i + 1] ?? 0;
      const b = img.data[i + 2] ?? 0;
      gray[y * size + x] = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    }
  }

  let total = 0;
  let count = 0;
  for (let y = 1; y < size - 1; y++) {
    for (let x = 1; x < size - 1; x++) {
      const center = gray[y * size + x]!;
      const lap =
        Math.abs(
          gray[(y - 1) * size + x]! +
          gray[(y + 1) * size + x]! +
          gray[y * size + x - 1]! +
          gray[y * size + x + 1]! -
          4 * center
        );
      total += lap;
      count++;
    }
  }

  return count > 0 ? total / count : null;
}

function applyScenarioPreset(
  tuning: FaceRenderTuning,
  scenario: FaceQualityScenario
): void {
  if (scenario === 'medium') {
    tuning.skinStrength = Math.min(tuning.skinStrength, 0.38);
    tuning.cheekOpacityScale *= 0.94;
    tuning.cheekRadiusScale *= 0.96;
    tuning.cheekDrapeScale *= 0.96;
    tuning.eyeOpacityScale *= 0.93;
    tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.12);
    tuning.eyeFeatherScale *= 0.96;
    tuning.eyeSparkleScale *= 0.95;
    tuning.lipFinishScale *= 0.96;
    return;
  }

  if (scenario === 'borderline') {
    tuning.skinStrength = Math.min(tuning.skinStrength, 0.36);
    tuning.cheekOpacityScale *= 0.88;
    tuning.cheekRadiusScale *= 0.92;
    tuning.cheekDrapeScale *= 0.9;
    tuning.eyeOpacityScale *= 0.86;
    tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.24);
    tuning.eyeFeatherScale *= 0.92;
    tuning.eyeSparkleScale *= 0.88;
    tuning.lipFinishScale *= 0.92;
  }
}

export function analyzeFaceQuality(
  source: ImageBitmap | HTMLCanvasElement | HTMLImageElement,
  detection: LipDetection
): FaceQualityReport {
  const qcopy = copyNow().quality;
  const warnings: string[] = [];
  const tuning: FaceRenderTuning = { ...DEFAULT_TUNING };

  const absYaw = Math.abs(detection.pose.yaw);
  const absPitch = Math.abs(detection.pose.pitch);
  const templeWidth = distance(pointPx(detection, 234), pointPx(detection, 454));
  const bounds = landmarkBounds(detection);
  const bboxWidth = Math.max(1, bounds.maxX - bounds.minX);
  const faceWidthPx = templeWidth ?? bboxWidth;
  const faceRatio = faceWidthPx / Math.max(1, detection.imageWidth);

  const mouthWidth =
    distance(pointPx(detection, 78), pointPx(detection, 308)) ??
    distance(pointPx(detection, 61), pointPx(detection, 291)) ??
    1;
  const mouthGap = distance(pointPx(detection, 13), pointPx(detection, 14)) ?? 0;
  const mouthRatio = mouthGap / Math.max(1, mouthWidth);
  const lighting = sampleLighting(source, detection);
  const blurScore = sampleBlurScore(source, detection);

  if (absYaw > 0.78 || absPitch > 0.52) {
    return {
      blocker: qcopy.blockers.pose,
      blockerCode: 'pose',
      scenario: 'borderline',
      warnings,
      tuning
    };
  }

  if (mouthRatio > 0.3) {
    return {
      blocker: qcopy.blockers.mouth,
      blockerCode: 'mouth',
      scenario: 'borderline',
      warnings,
      tuning
    };
  }

  if (faceRatio < 0.12) {
    return {
      blocker: qcopy.blockers.distance,
      blockerCode: 'distance',
      scenario: 'borderline',
      warnings,
      tuning
    };
  }

  let risk = 0;
  if (absYaw > 0.42 || absPitch > 0.3) risk += 1;
  if (absYaw > 0.54 || absPitch > 0.38) risk += 1;
  if (mouthRatio > 0.12) risk += 1;
  if (faceRatio < 0.2) risk += 1;
  if (lighting && (lighting.mean < 0.3 || lighting.splitDelta > 0.16 || lighting.variance > 0.04)) risk += 1;
  if (blurScore !== null && blurScore < 0.042) risk += 2;
  else if (blurScore !== null && blurScore < 0.065) risk += 1;

  let scenario: FaceQualityScenario = 'clean';
  if (risk >= 4) scenario = 'borderline';
  else if (risk >= 2) scenario = 'medium';

  applyScenarioPreset(tuning, scenario);

  if (absYaw > 0.48 || absPitch > 0.34) {
    warnings.push(qcopy.warnings.tilted);
    tuning.cheekOpacityScale *= 0.78;
    tuning.cheekRadiusScale *= 0.92;
    tuning.cheekDrapeScale *= 0.9;
    tuning.eyeOpacityScale *= 0.72;
    tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.35);
    tuning.eyeFeatherScale *= 0.9;
    tuning.eyeSparkleScale *= 0.88;
    tuning.lipFinishScale *= 0.88;
  }

  if (absYaw > 0.58 || absPitch > 0.4) {
    warnings.push(qcopy.warnings.poseLimit);
    tuning.cheekOpacityScale *= 0.8;
    tuning.cheekRadiusScale *= 0.86;
    tuning.cheekDrapeScale *= 0.84;
    tuning.eyeOpacityScale *= 0.78;
    tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.58);
    tuning.eyeFeatherScale *= 0.86;
    tuning.eyeSparkleScale *= 0.82;
    tuning.lipFinishScale *= 0.9;
  }

  if (mouthRatio > 0.14) {
    warnings.push(qcopy.warnings.mouthOpen);
    tuning.lipOpacityScale *= 0.94;
    tuning.lipInnerHoleScale = 1.32;
    tuning.lipFinishScale *= 0.86;
  } else if (mouthRatio > 0.09) {
    tuning.lipInnerHoleScale = 1.18;
    tuning.lipFinishScale *= 0.92;
  }

  if (faceRatio < 0.2) {
    warnings.push(qcopy.warnings.faceFar);
    tuning.cheekOpacityScale *= 0.86;
    tuning.cheekRadiusScale *= 0.9;
    tuning.eyeOpacityScale *= 0.82;
    tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.28);
    tuning.eyeFeatherScale *= 0.92;
  }

  if (lighting) {
    if (lighting.mean < 0.3) {
      warnings.push(qcopy.warnings.lowLight);
      tuning.skinStrength = 0.32;
      tuning.cheekOpacityScale *= 0.88;
      tuning.cheekRadiusScale *= 0.94;
      tuning.eyeOpacityScale *= 0.9;
      tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.22);
      tuning.eyeSparkleScale *= 0.9;
      tuning.lipFinishScale *= 0.92;
    } else if (lighting.splitDelta > 0.16 || lighting.variance > 0.04) {
      warnings.push(qcopy.warnings.unevenLight);
      tuning.skinStrength = 0.34;
      tuning.cheekOpacityScale *= 0.84;
      tuning.cheekRadiusScale *= 0.88;
      tuning.cheekDrapeScale *= 0.9;
      tuning.eyeOpacityScale *= 0.8;
      tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.42);
      tuning.eyeFeatherScale *= 0.88;
      tuning.eyeSparkleScale *= 0.84;
      tuning.lipFinishScale *= 0.9;
    }
  }

  if (blurScore !== null && blurScore < 0.042) {
    warnings.push(qcopy.warnings.softPhoto);
  } else if (blurScore !== null && blurScore < 0.065) {
    warnings.push(qcopy.warnings.notSharp);
  }

  tuning.skinStrength = clamp(tuning.skinStrength, 0.28, 0.42);
  tuning.cheekOpacityScale = clamp(tuning.cheekOpacityScale, 0.5, 1);
  tuning.cheekRadiusScale = clamp(tuning.cheekRadiusScale, 0.78, 1);
  tuning.cheekDrapeScale = clamp(tuning.cheekDrapeScale, 0.78, 1);
  tuning.eyeOpacityScale = clamp(tuning.eyeOpacityScale, 0.46, 1);
  tuning.eyeShapeTightness = clamp(tuning.eyeShapeTightness, 0, 0.7);
  tuning.eyeFeatherScale = clamp(tuning.eyeFeatherScale, 0.78, 1);
  tuning.eyeSparkleScale = clamp(tuning.eyeSparkleScale, 0.76, 1);
  tuning.lipOpacityScale = clamp(tuning.lipOpacityScale, 0.9, 1);
  tuning.lipInnerHoleScale = clamp(tuning.lipInnerHoleScale, 1, 1.38);
  tuning.lipFinishScale = clamp(tuning.lipFinishScale, 0.72, 1);

  return {
    blocker: null,
    blockerCode: null,
    scenario,
    warnings,
    tuning
  };
}
